/**
 * EventFlowCard — Visual event workflow timeline.
 *
 * Displays 4 steps: Event Start → Accepted → Cleared → Finished
 * with calculated durations between each step.
 *
 * Props:
 *   eventData — main event record object
 *   tzOffset  — timezone offset for datetime display
 */

import {convertIsoToLocal, convertIsoUTCToLocal, strTimeDifference} from '../../../../libs.jsx';

const EventFlowCard = ({ eventData, tzOffset }) => {
    if (!eventData) return null;

    const d = eventData;

    // Datetimes
    const eventDt = d.event_datetime_local || null;
    const acceptDt = d.accept_datetime_local || null;
    const ackDt = d.ack_datetime_local || null;
    const finishDt = d.finish_datetime_local || null;
    const autoclearDt = d.autoclear_datetime_local || null;

    // Format a datetime for display
    const fmt = (dt) => dt ? convertIsoToLocal(dt) : '\u2014';

    // Calculate duration between two ISO datetime strings
    // strTimeDifference already returns a formatted string via secondToHourMinSec
    const calcDuration = (start, end) => {
        if (!start || !end) return null;
        const result = strTimeDifference(start, end);
        if (!result || result === 'N/A') return null;
        return result.trim();
    };

    // Durations
    const timeToAccept = calcDuration(eventDt, acceptDt);
    const arrivalTime = calcDuration(acceptDt, ackDt);
    const serviceTime = calcDuration(ackDt, finishDt);
    const totalEventTime = calcDuration(eventDt, finishDt || ackDt);
    const responseTime = calcDuration(eventDt, ackDt);

    // Step definitions
    const steps = [
        { key: 'event', label: 'Event Start', time: eventDt, completed: !!eventDt },
        { key: 'accept', label: 'Accepted', time: acceptDt, completed: !!acceptDt },
        { key: 'clear', label: 'Cleared', time: ackDt, completed: !!ackDt, badge: autoclearDt ? 'Auto-cleared' : null },
        { key: 'finish', label: 'Finished', time: finishDt, completed: !!finishDt },
    ];

    // Connector definitions (between steps)
    const connectors = [
        { key: 'to-accept', label: 'Time to Accept', duration: timeToAccept, pending: !acceptDt },
        { key: 'arrival', label: 'Arrival Time', duration: arrivalTime, pending: !ackDt },
        { key: 'service', label: 'Service Time', duration: serviceTime, pending: !finishDt },
    ];

    // Step numbering
    const stepNum = (idx) => idx + 1;

    return (
        <div className="pp-section">
            <div className="pp-section__header">
                <h3 className="pp-section__title">Event Flow</h3>
            </div>
            <div className="pp-section__body">
                {/* Timeline */}
                <div className="ep-flow-timeline">
                    {steps.map((step, idx) => (
                        <div key={step.key} style={{ display: 'contents' }}>
                            {/* Step node */}
                            <div className={`ep-flow-step${step.completed ? '' : ' ep-flow-step--pending'}`}>
                                <div className="ep-flow-step__dot">{stepNum(idx)}</div>
                                <div className="ep-flow-step__label">{step.label}</div>
                                <div className="ep-flow-step__time">{fmt(step.time)}</div>
                                {step.badge ? (
                                    <div className="ep-flow-step__badge">{step.badge}</div>
                                ) : null}
                            </div>

                            {/* Connector (between steps, not after last) */}
                            {idx < connectors.length ? (
                                <div className={`ep-flow-connector${connectors[idx].pending ? ' ep-flow-connector--pending' : ''}`}>
                                    <div className="ep-flow-connector__line" />
                                    <div className="ep-flow-connector__label">{connectors[idx].label}</div>
                                    <div className="ep-flow-connector__duration">
                                        {connectors[idx].duration || '\u2014'}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    ))}
                </div>

                {/* Summary totals */}
                <div className="ep-flow-totals">
                    <div className="ep-flow-total">
                        <span className="ep-flow-total__label">Total Event Time</span>
                        <span className="ep-flow-total__value">{totalEventTime || '\u2014'}</span>
                    </div>
                    <div className="ep-flow-total">
                        <span className="ep-flow-total__label">Response Time</span>
                        <span className="ep-flow-total__value">{responseTime || '\u2014'}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventFlowCard;
